import {
  formatNumberText,
  numberIsValid,
  numberRangeError,
  parseNumberText,
  sameNumber,
  sanitizeNumberText,
} from '@sportpark/shared/number-text';
import { type Component, type JSX, Show, createEffect, createSignal, on, splitProps } from 'solid-js';

export {
  formatNumberText,
  numberIsValid,
  numberRangeError,
  parseNumberText,
  sanitizeNumberText,
  type NumberRange,
} from '@sportpark/shared/number-text';

export interface NumberInputProps
  extends Omit<
    JSX.InputHTMLAttributes<HTMLInputElement>,
    'value' | 'type' | 'inputmode' | 'inputMode' | 'onInput' | 'onChange' | 'min' | 'max' | 'step'
  > {
  value: number | null;
  onValue: (value: number | null) => void;
  decimals?: number;
  allowNegative?: boolean;
  min?: number;
  max?: number;
  error?: string | null;
}

let uid = 0;

export const NumberInput: Component<NumberInputProps> = (props) => {
  const [local, rest] = splitProps(props, [
    'value',
    'onValue',
    'decimals',
    'allowNegative',
    'min',
    'max',
    'error',
    'onBlur',
    'style',
    'id',
  ]);
  const decimals = () => local.decimals ?? 0;
  const id = local.id ?? `num-${++uid}`;
  const [text, setText] = createSignal(formatNumberText(local.value));

  createEffect(
    on(
      () => local.value,
      (value) => {
        if (!sameNumber(value, parseNumberText(text()))) setText(formatNumberText(value));
      },
    ),
  );

  const message = () =>
    local.error ?? numberRangeError(local.value, { min: local.min, max: local.max });

  const style = (): JSX.CSSProperties | string | undefined => {
    if (!message()) return local.style;
    const ring = { 'box-shadow': 'inset 0 0 0 1px var(--color-danger)' };
    return typeof local.style === 'string'
      ? `${local.style};box-shadow:inset 0 0 0 1px var(--color-danger)`
      : { ...(local.style ?? {}), ...ring };
  };

  return (
    <>
      <input
        autocomplete="off"
        {...rest}
        id={id}
        type="text"
        inputmode={decimals() > 0 ? 'decimal' : 'numeric'}
        value={text()}
        style={style()}
        aria-invalid={message() ? true : undefined}
        aria-describedby={message() ? `${id}-error` : undefined}
        onInput={(e) => {
          const el = e.currentTarget;
          const raw = el.value;
          const clean = sanitizeNumberText(raw, decimals(), local.allowNegative);
          if (clean !== raw) {
            const caret = Math.max(0, (el.selectionStart ?? raw.length) - (raw.length - clean.length));
            el.value = clean;
            el.setSelectionRange(caret, caret);
          }
          setText(clean);
          local.onValue(parseNumberText(clean));
        }}
        onBlur={(e) => {
          setText(formatNumberText(parseNumberText(text())));
          if (typeof local.onBlur === 'function') local.onBlur(e);
        }}
      />
      <Show when={message()}>
        <span id={`${id}-error`} role="alert" class="text-[11px] leading-snug text-danger">
          {message()}
        </span>
      </Show>
    </>
  );
};

export interface NumberCommitInputProps extends Omit<NumberInputProps, 'onValue'> {
  onCommit: (value: number) => void;
}

export const NumberCommitInput: Component<NumberCommitInputProps> = (props) => {
  const [local, rest] = splitProps(props, ['value', 'onCommit', 'onBlur', 'onKeyDown']);
  const [draft, setDraft] = createSignal<number | null>(local.value);
  createEffect(on(() => local.value, (v) => setDraft(v)));

  return (
    <NumberInput
      {...rest}
      value={draft()}
      onValue={setDraft}
      onKeyDown={(e) => {
        if (e.key === 'Enter') e.currentTarget.blur();
        if (typeof local.onKeyDown === 'function') local.onKeyDown(e);
      }}
      onBlur={(e) => {
        const n = draft();
        if (numberIsValid(n, { min: props.min, max: props.max })) {
          if (!sameNumber(n, local.value)) local.onCommit(n);
        } else {
          setDraft(local.value);
        }
        if (typeof local.onBlur === 'function') local.onBlur(e);
      }}
    />
  );
};
